import React, { useRef, useCallback } from 'react';
import DropDownBox from 'devextreme-react/drop-down-box';
import DataGrid, { Column, Scrolling, Selection, Paging } from 'devextreme-react/data-grid';

type ColumnDef = {
  dataField: string;
  caption?: string;
  width?: number | string;
};

export interface DropDownGridProps<T = any> {
  dataSource: any; // array or DataSource
  value?: any;
  valueExpr?: string;
  displayExpr?: string | ((item: T) => string);
  placeholder?: string;
  width?: number | string;
  dropDownWidth?: number | string;
  dropDownHeight?: number | string;
  onValueChanged?: (e: { value: any }) => void;
  columns?: ColumnDef[]; // if not provided, displayExpr will be used
  keyExpr?: string;
}

export const DropDownGrid = <T,>(props: DropDownGridProps<T>) => {
  const { dataSource, value, valueExpr = 'Id', displayExpr, placeholder, width, dropDownWidth = 700, dropDownHeight = 400, onValueChanged, columns, keyExpr = valueExpr } = props;

  const boxRef = useRef<DropDownBox>(null);

  const handleSelectionChanged = useCallback(
    (e: any) => {
      const selected = e.selectedRowsData && e.selectedRowsData[0];
      if (!selected) return;
      const v = selected[valueExpr];
      onValueChanged && onValueChanged({ value: v });
      try {
        boxRef.current?.instance.close();
      } catch (err) {
        // ignore
      }
    },
    [onValueChanged, valueExpr]
  );

  const contentRender = useCallback(() => {
    return (
      <div style={{ padding: 8 }}>
        <DataGrid
          dataSource={dataSource}
          keyExpr={keyExpr}
          showBorders={false}
          height={dropDownHeight}
          onSelectionChanged={handleSelectionChanged}
          selectedRowKeys={value != null ? [value] : []}
        >
          <Selection mode='single' />
          <Scrolling mode='virtual' />
          <Paging enabled={false} />
          {columns && columns.length > 0 ? (
            columns.map((c) => <Column key={c.dataField} dataField={c.dataField} caption={c.caption} width={c.width} />)
          ) : (
            <Column dataField={typeof displayExpr === 'string' ? displayExpr : valueExpr} caption={typeof displayExpr === 'string' ? displayExpr : ''} />
          )}
        </DataGrid>
      </div>
    );
  }, [dataSource, keyExpr, handleSelectionChanged, dropDownHeight, columns, displayExpr, valueExpr, value]);

  return (
    <DropDownBox
      ref={boxRef}
      value={value}
      valueExpr={valueExpr}
      displayExpr={displayExpr as any}
      placeholder={placeholder}
      dataSource={dataSource}
      deferRendering={false}
      contentRender={contentRender as any}
      dropDownOptions={{ width: dropDownWidth, height: dropDownHeight, showTitle: false }}
      width={width}
    />
  );
};

export default DropDownGrid;
